import React, { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { createPortal } from 'react-dom';

export const DeleteMessageDialog = ({
  open,
  onClose,
  onDeleteForMe,
  onDeleteForEveryone,
  canDeleteForEveryone = true,
  messageCount = 1,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleDelete = async (action) => {
    setIsDeleting(true);
    setError('');
    try {
      const succeeded = await action();
      if (succeeded === false) {
        setError('Mesazhi nuk u fshi. Provoni përsëri.');
        return;
      }
      onClose();
    } catch (deleteError) {
      console.error('Unable to delete chat message:', deleteError);
      setError('Mesazhi nuk u fshi. Provoni përsëri.');
    } finally {
      setIsDeleting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isDeleting) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-message-title"
        aria-describedby="delete-message-description"
        className="w-full max-w-md overflow-hidden rounded-2xl border-0 bg-white text-zinc-950 shadow-2xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
              <Trash2 size={18} />
            </span>
            <div>
              <h2 id="delete-message-title" className="text-sm font-bold text-zinc-950">
                {messageCount > 1 ? 'Fshi mesazhet' : 'Fshi mesazhin'}
              </h2>
              <p id="delete-message-description" className="mt-1 text-xs leading-relaxed text-zinc-600">
                {canDeleteForEveryone
                  ? 'Zgjidhni si dëshironi t’i fshini mesazhet.'
                  : 'Disa mesazhe janë dërguar nga pjesëmarrës të tjerë. Mund t’i hiqni vetëm nga biseda juaj.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="shrink-0 rounded-lg p-1.5 text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-50"
            aria-label="Mbyll dritaren"
            title="Mbyll"
          >
            <X size={18} />
          </button>
        </header>

        <div className="space-y-2 p-5">
          <button
            type="button"
            disabled={isDeleting}
            onClick={() => handleDelete(() => onDeleteForMe())}
            className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-left transition hover:border-amber-300 hover:bg-amber-50/60 disabled:opacity-50"
          >
            <span>
              <span className="block text-xs font-bold text-zinc-900">Fshi për mua</span>
              <span className="mt-0.5 block text-[11px] text-zinc-500">Hiq vetëm nga biseda jote</span>
            </span>
            <Trash2 size={15} className="shrink-0 text-amber-700" />
          </button>

          {canDeleteForEveryone && (
            <button
              type="button"
              disabled={isDeleting}
              onClick={() => handleDelete(onDeleteForEveryone)}
              className="flex w-full items-center justify-between rounded-xl border border-red-200 bg-white px-3.5 py-3 text-left transition hover:border-red-300 hover:bg-red-50/60 disabled:opacity-50"
            >
              <span>
                <span className="block text-xs font-bold text-red-700">Fshi për të gjithë</span>
                <span className="mt-0.5 block text-[11px] text-red-600/80">Hiq te të gjithë pjesëmarrësit</span>
              </span>
              <Trash2 size={15} className="shrink-0 text-red-600" />
            </button>
          )}
          {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
        </div>

        <footer className="flex justify-end border-t border-zinc-100 bg-zinc-50 px-5 py-3">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isDeleting} className="rounded-xl text-xs text-zinc-700 hover:bg-zinc-200">
            Anulo
          </Button>
        </footer>
      </section>
    </div>,
    document.body
  );
};

export default DeleteMessageDialog;
